import packageMetadata from "../../package.json";
import {
  createTelemetryDeduper,
  createTelemetryEvent,
  serializeTelemetryEvent,
  type TelemetryContext,
  type TelemetryDeduper,
  type TelemetryEventName,
  type TelemetryEventPayload,
  type TelemetryReleaseChannel
} from "./schema";
import { TELEMETRY_ENDPOINT } from "./schema";

export { createTelemetryDeduper } from "./schema";
export type {
  TelemetryContext,
  TelemetryDeduper,
  TelemetryEventName,
  TelemetryEventPayload,
  TelemetryReleaseChannel
} from "./schema";

type FetchLike = typeof fetch;

export interface TelemetryDispatchOptions {
  deduper?: TelemetryDeduper;
  endpoint?: string;
  fetcher?: FetchLike;
}

export interface BrowserReleaseContext {
  release_version: string;
  release_channel: TelemetryReleaseChannel;
}

export function resolveReleaseChannel(mode: string): TelemetryReleaseChannel {
  if (mode === "production") return "production";
  if (mode === "development" || mode === "test") return "development";
  return "preview";
}

export function currentReleaseContext(overrides?: {
  releaseVersion?: string;
  releaseChannel?: TelemetryReleaseChannel;
}): BrowserReleaseContext {
  return {
    release_version: overrides?.releaseVersion ?? packageMetadata.version,
    release_channel:
      overrides?.releaseChannel ?? resolveReleaseChannel(import.meta.env.MODE)
  };
}

export function prepareTelemetryEvent(
  event: TelemetryEventName,
  context: Omit<TelemetryContext, "release_version" | "release_channel"> &
    Partial<Pick<TelemetryContext, "release_version" | "release_channel">>,
  overrides?: {
    releaseVersion?: string;
    releaseChannel?: TelemetryReleaseChannel;
  }
): TelemetryEventPayload | null {
  const release = currentReleaseContext(overrides);
  return createTelemetryEvent(event, {
    ...context,
    release_version: context.release_version ?? release.release_version,
    release_channel: context.release_channel ?? release.release_channel
  });
}

export async function postTelemetryEvent(
  payload: unknown,
  options?: TelemetryDispatchOptions
): Promise<boolean> {
  const body = serializeTelemetryEvent(payload);
  if (body === null) return false;

  if (options?.deduper && !options.deduper.claim(payload)) {
    return false;
  }

  const fetcher = options?.fetcher ?? globalThis.fetch;
  if (typeof fetcher !== "function") return false;

  try {
    const response = await fetcher(options?.endpoint ?? TELEMETRY_ENDPOINT, {
      method: "POST",
      body,
      cache: "no-store",
      credentials: "omit",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json"
      },
      keepalive: true,
      mode: "same-origin",
      referrerPolicy: "no-referrer"
    });
    return response.status === 204;
  } catch {
    return false;
  }
}

export const telemetryClient = {
  createDeduper: createTelemetryDeduper,
  post: postTelemetryEvent,
  prepare: prepareTelemetryEvent
};
